import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useAttendeeData } from '../../hooks/useAttendeeData';
import { getGreeting } from '../../utils/attendeeUtils';
import NextEventCard from '../../components/attendee/NextEventCard';
import EmptyState from '../../components/attendee/EmptyState';
import LoadingState from '../../components/attendee/LoadingState';
import NotificationPanel from '../../components/NotificationPanel';
import { formatDate, getDaysToGo } from '../../utils/attendeeUtils';

const AttendeeHome = () => {
  const { user } = useAuth();
  const {
    loading,
    error,
    profile,
    registrations,
    upcomingRegistrations,
    savedEvents,
    favoriteSpeakers,
    recommendedEvents,
    nextEvent,
    totalSpent,
  } = useAttendeeData();

  const [greeting, setGreeting] = useState(getGreeting());

  useEffect(() => {
    setGreeting(getGreeting());
  }, []);

  const firstName = profile?.full_name?.split(' ')[0] || user?.email?.split('@')[0] || 'Attendee';
  const nextRegistration = upcomingRegistrations[0];

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-64 animate-pulse mb-2" />
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-48 animate-pulse" />
        </div>
        <LoadingState variant="hero" />
        <LoadingState count={3} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <EmptyState
          title="Something went wrong"
          message="We couldn't load your dashboard. Please try again."
          ctaText="Retry"
          ctaLink="/attendee"
        />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Greeting */}
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
          {greeting}, {firstName}
        </h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">
          Here's what's happening with your events.
        </p>
      </div>

      {/* Next Event Card - most prominent */}
      {nextEvent && nextRegistration ? (
        <NextEventCard event={nextEvent} registration={nextRegistration} />
      ) : (
        <div className="mb-8">
          <EmptyState
            title="No upcoming events"
            message="You don't have any upcoming events yet. Browse events and register to get started."
            ctaText="Browse Events"
            ctaLink="/events"
          />
        </div>
      )}

      {/* Important Notifications */}
      {user && (
        <div className="mb-8">
          <NotificationPanel userId={user.id} userType="attendee" />
        </div>
      )}

      {/* Other Upcoming Events */}
      {upcomingRegistrations.length > 1 && (
        <section className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">
              Other Upcoming Events
            </h2>
            <Link to="/attendee/events" className="text-sm text-primary hover:text-primary-dark font-medium">
              View all →
            </Link>
          </div>
          <div className="space-y-3">
            {upcomingRegistrations.slice(1, 4).map(reg => (
              <NextEventCard
                key={reg.id}
                event={reg.events}
                registration={reg}
                variant="compact"
              />
            ))}
          </div>
        </section>
      )}

      {/* Recommended Events */}
      {recommendedEvents.length > 0 && (
        <section className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                Recommended for You
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">Based on your interests</p>
            </div>
            <Link to="/events" className="text-sm text-primary hover:text-primary-dark font-medium">
              Explore →
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {recommendedEvents.map(event => (
              <Link
                key={event.id}
                to={`/events/${event.id}`}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden hover:shadow-md transition-shadow group"
              >
                {event.image_url && (
                  <div className="h-32 overflow-hidden">
                    <img src={event.image_url} alt={event.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                  </div>
                )}
                <div className="p-4">
                  <h3 className="font-semibold text-gray-900 dark:text-white text-sm truncate">
                    {event.title}
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {formatDate(event.start_date)}
                  </p>
                  <p className="text-xs text-primary mt-1">{getDaysToGo(event.start_date)}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Saved Events Preview */}
      {savedEvents.length > 0 && (
        <section className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">
              Saved Events
            </h2>
            <Link to="/attendee/events" className="text-sm text-primary hover:text-primary-dark font-medium">
              View all →
            </Link>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-2">
            {savedEvents.slice(0, 5).map(event => (
              <Link
                key={event.id}
                to={`/events/${event.id}`}
                className="flex-shrink-0 w-64 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden hover:shadow-md transition-shadow"
              >
                {event.image_url && (
                  <img src={event.image_url} alt={event.title} className="w-full h-24 object-cover" />
                )}
                <div className="p-3">
                  <h3 className="font-medium text-gray-900 dark:text-white text-sm truncate">
                    {event.title}
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {formatDate(event.start_date)}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Favorite Speakers Preview */}
      {favoriteSpeakers.length > 0 && (
        <section className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">
              Favorite Speakers
            </h2>
            <Link to="/speakers" className="text-sm text-primary hover:text-primary-dark font-medium">
              Browse speakers →
            </Link>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-2">
            {favoriteSpeakers.slice(0, 5).map(speaker => (
              <Link
                key={speaker.id}
                to={`/speakers/${speaker.id}`}
                className="flex-shrink-0 w-40 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 text-center hover:shadow-md transition-shadow"
              >
                <img
                  src={speaker.photo_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(speaker.name)}&background=7c3aed&color=fff&size=64`}
                  alt={speaker.name}
                  className="w-14 h-14 rounded-full object-cover mx-auto mb-2"
                />
                <h3 className="font-medium text-gray-900 dark:text-white text-sm truncate">
                  {speaker.name}
                </h3>
                {speaker.title && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                    {speaker.title}
                  </p>
                )}
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Stats - lower on page, not dominant */}
      <section className="mb-8">
        <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
          Your Activity
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <StatCard label="Registrations" value={registrations.filter(r => r.status !== 'cancelled').length} />
          <StatCard label="Saved Events" value={savedEvents.length} />
          <StatCard label="Favorite Speakers" value={favoriteSpeakers.length} />
          <StatCard label="Upcoming" value={upcomingRegistrations.length} />
          <StatCard label="Total Spent" value={totalSpent > 0 ? `$${totalSpent.toFixed(0)}` : '$0'} />
        </div>
      </section>
    </div>
  );
};

const StatCard = ({ label, value }) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
    <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{label}</p>
  </div>
);

export default AttendeeHome;
